import { scaleApparent } from "@/lib/units";
import { useRealtimeTotal } from "@/hooks/useRealtimeTotal";
import { cn } from "@/lib/utils";

// TotalLoadWidget — the prominent headline on the dyno dashboard. Shows the
// realtime total show load (summed live from the WS snapshot) and the peak
// over the chart's selected range, both in amps and kVA, for sizing a
// generator.
//
// Realtime comes from useRealtimeTotal (live, always all active services).
// Range-max is passed in by the section container, which owns the single
// history fetch shared with the chart.

/** Amps: integers at >= 10 A, one decimal below — small loads still read. */
function formatAmps(a: number): string {
  return a >= 10 ? String(Math.round(a)) : a.toFixed(1);
}

/** kVA/VA via the shared ServiceTile scaler, so the widget matches the
 * per-service tiles' apparent-power formatting. */
function formatApparent(va: number): string {
  const s = scaleApparent(va);
  return `${s.value.toFixed(s.digits)} ${s.unit}`;
}

/** Resolve a range-max figure to display text: an error reads "unavailable",
 * a pending fetch reads "loading…", otherwise the formatted value. */
function rangeMaxText(
  historyError: boolean | undefined,
  render: (v: number) => string,
  v: number | null,
): { text: string; muted: boolean } {
  if (historyError) return { text: "unavailable", muted: true };
  if (v == null) return { text: "loading…", muted: true };
  return { text: render(v), muted: false };
}

interface TotalLoadWidgetProps {
  /** The selected event's membership system_ids. The realtime "Now" figures
   * sum only these services — the admin WS feed carries every active event's.
   * Optional only for test ergonomics; TotalLoadSection always supplies it in
   * production. Omitted → sums every service in the feed (legacy). */
  eventSystemIds?: ReadonlySet<string>;
  /** Peak total apparent power (VA) over the selected range; null while the
   * history fetch is pending. */
  rangeMaxApparentVa: number | null;
  /** Peak total max-leg current (A) over the selected range; null while
   * pending. */
  rangeMaxAmps: number | null;
  /** Human label for the selected range, e.g. "1h". */
  rangeLabel: string;
  /** History fetch failed — show "unavailable" rather than a stale max. */
  historyError?: boolean;
}

function Metric({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "tabular-nums font-semibold",
          muted ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function TotalLoadWidget({
  eventSystemIds,
  rangeMaxApparentVa,
  rangeMaxAmps,
  rangeLabel,
  historyError,
}: TotalLoadWidgetProps) {
  const rt = useRealtimeTotal(eventSystemIds);

  // No active ACUVIM services in the live feed — muted placeholder rather
  // than a misleading 0 kVA / 0 A headline.
  if (rt.serviceCount === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">Total Show Load</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          No active ACUVIM services.
        </p>
      </div>
    );
  }

  const maxKva = rangeMaxText(historyError, formatApparent, rangeMaxApparentVa);
  const maxAmps = rangeMaxText(
    historyError,
    (a) => `${formatAmps(a)} A`,
    rangeMaxAmps,
  );

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Total Show Load</h2>
        {rt.stale && (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-500">
            Stale feed
          </span>
        )}
      </div>

      {/* Realtime — the large, prominent figures. */}
      <div className="mt-2 flex items-end gap-6">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Now
          </span>
          <span
            className={cn(
              "text-3xl font-bold tabular-nums",
              rt.stale ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {formatApparent(rt.apparentVa)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Max leg
          </span>
          <span
            className={cn(
              "text-3xl font-bold tabular-nums",
              rt.stale ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {formatAmps(rt.ampsMaxLeg)} A
          </span>
        </div>
      </div>

      {/* Range peak — sized from the selected chart range. */}
      <div className="mt-3 flex gap-6 border-t border-border pt-2">
        <Metric label={`Peak · ${rangeLabel}`} value={maxKva.text} muted={maxKva.muted} />
        <Metric label={`Peak A · ${rangeLabel}`} value={maxAmps.text} muted={maxAmps.muted} />
      </div>
    </div>
  );
}
